import React, { useState, useRef, useEffect } from "react";
import { useDisplayScale } from "../helpers/DisplayScaleContext";

const DisplayScaleControl = () => {
  const {
    scale,
    scalePercentage,
    minScale,
    maxScale,
    zoomIn,
    zoomOut,
    resetScale,
  } = useDisplayScale();

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={containerRef} className="fixed bottom-3 right-3 z-[9999]">
      {isOpen ? (
        <div
          className="
            inline-flex items-center
            rounded-md
            border border-slate-200
            bg-white/95
            px-0.5 py-0.5
            shadow-sm
            backdrop-blur-sm
          "
          aria-label="Page display size controls"
        >
          {/* Zoom Out */}
          <button
            type="button"
            onClick={zoomOut}
            disabled={scale <= minScale}
            className="
              flex h-5 w-5 items-center justify-center
              rounded
              text-xs font-semibold text-slate-600
              transition-colors
              hover:bg-slate-100
              disabled:cursor-not-allowed
              disabled:opacity-35
            "
            aria-label="Decrease display size"
          >
            −
          </button>

          {/* Current Scale / Reset */}
          <button
            type="button"
            onClick={resetScale}
            className="
              min-w-[32px]
              rounded
              px-1 py-0.5
              text-[9px] font-semibold
              text-slate-700
              transition-colors
              hover:bg-slate-100
            "
            title="Reset display size"
            aria-label={`Reset display size to 100%. Current size is ${scalePercentage}%`}
          >
            {scalePercentage}%
          </button>

          {/* Zoom In */}
          <button
            type="button"
            onClick={zoomIn}
            disabled={scale >= maxScale}
            className="
              flex h-5 w-5 items-center justify-center
              rounded
              text-xs font-semibold text-slate-600
              transition-colors
              hover:bg-slate-100
              disabled:cursor-not-allowed
              disabled:opacity-35
            "
            aria-label="Increase display size"
          >
            +
          </button>

          {/* Collapse */}
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="
              flex h-5 w-5 items-center justify-center
              rounded
              text-slate-400
              transition-colors
              hover:bg-slate-100 hover:text-slate-600
            "
            aria-label="Close display size controls"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-3 w-3"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="
            flex h-7 w-7 items-center justify-center
            rounded-full
            border border-slate-200
            bg-white/95
            text-slate-600
            shadow-sm
            backdrop-blur-sm
            transition-colors
            hover:bg-slate-100
          "
          aria-label={`Open display size controls. Current size is ${scalePercentage}%`}
          title="Display size"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-3.5 w-3.5"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="8" y1="11" x2="14" y2="11" />
            <line x1="11" y1="8" x2="11" y2="14" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default DisplayScaleControl;